"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    chiffreDAffairesAnnualise,
    composerReclamation,
    concerne,
    delaiOuvert,
    montantLisible,
    periodeDeReference,
    pieces,
    recapitulatif,
    valider,
} from "@/lib/tools/cfeRefundClaim";
import { documentEnTexte, nomDuFichier } from "@/lib/tools/documentText";
import { telechargerDocument } from "@/lib/tools/documentPdf";
import { ApercuDocument } from "./DocumentPaper";
import { PadSignature, useSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Liste,
    Recommencer,
    Resultat,
    Segments,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * La réclamation contentieuse de CFE.
 *
 * DEUX TRIS QUI ÉPARGNENT UN APRÈS-MIDI. Le premier est le motif : quatre
 * seulement ouvrent un dégrèvement à un micro-entrepreneur, et « je trouve que
 * c'est trop cher » n'en fait pas partie. Le second est le délai de l'article
 * R*196-2, qui se calcule à partir de l'année d'imposition et ferme au 31
 * décembre de l'année suivante. Un troisième refus vient du calcul lui-même :
 * réclamer au titre des 5 000 € avec un chiffre d'affaires annualisé qui les
 * dépasse mène à un rejet, et l'outil le dit avant d'écrire la lettre.
 *
 * La signature est celle de la visite : c'est le contribuable qui réclame.
 * L'article R*197-3 exige d'ailleurs une signature manuscrite, ce que l'écran
 * rappelle, le paraphe tracé ne valant que pour un envoi par la messagerie
 * sécurisée de l'espace professionnel.
 */

const L = {
    fr: {
        phases: ["Votre situation", "Votre dossier", "Signature"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : la lettre s'y fabrique, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",
        motifTitre: "Pourquoi pensez-vous ne pas devoir cette CFE ?",
        motifAide: "Quatre motifs ouvrent un dégrèvement à un micro-entrepreneur. Chacun a son article et sa pièce justificative.",
        motifCreation: "C'était mon année de création",
        motifCreationDetail: "Aucune CFE n'est due cette année-là",
        motifPasDeCa: "Je n'avais encaissé aucun chiffre d'affaires",
        motifPasDeCaDetail: "L'avis est arrivé avant mon premier euro",
        motifSeuil: "Mon chiffre d'affaires est sous 5 000 €",
        motifSeuilDetail: "Exonération de la cotisation minimum",
        motifCessation: "J'ai cessé mon activité en cours d'année",
        motifCessationDetail: "Réduction au prorata des mois restants",
        motifAutre: "Une autre raison",
        motifAutreDetail: "Ou je ne sais pas",
        anneeTitre: "Quelle année d'imposition contestez-vous ?",
        anneeAide: "L'année inscrite sur l'avis, pas celle où vous l'avez payé. Le délai de réclamation en dépend.",
        champAnnee: "Année de l'avis de CFE",
        delaiRestant: "Cette imposition est réclamable jusqu'au {limite}.",
        datesTitre: "Les dates de votre entreprise",
        datesAide: "Elles fondent votre réclamation : le service des impôts les vérifiera sur votre avis de situation Sirene.",
        champDateCreation: "Date de création de l'entreprise",
        champDateCessation: "Date de cessation d'activité",
        aideDateCessation: "La date déclarée au guichet unique, dans l'année contestée.",
        champChiffreDAffaires: "Chiffre d'affaires de la période de référence",
        aideChiffreDAffaires: "En euros, hors taxes. La période de référence est l'avant-dernière année avant l'imposition, ou la dernière si votre entreprise n'existait pas encore.",
        horsSujetTitre: "Cette réclamation n'aboutirait pas",
        motifRefusAutre: "La cotisation foncière des entreprises se conteste sur un fondement précis, pas sur son montant. Quatre motifs seulement ouvrent un dégrèvement à un micro-entrepreneur :",
        motifsRecevables: [
            "L'année de création.",
            "L'absence de tout chiffre d'affaires.",
            "Un chiffre d'affaires de la période de référence inférieur ou égal à 5 000 €.",
            "La cessation d'activité en cours d'année.",
        ],
        motifRefusAutreFin: "Si aucun ne correspond, la cotisation est due, même quand elle paraît disproportionnée : la base minimum ne dépend pas de votre bénéfice.",
        motifRefusDelai: "Le délai de réclamation est passé. L'article R*196-2 du livre des procédures fiscales le fixe au 31 décembre de l'année qui suit la mise en recouvrement : pour la CFE {annee}, il s'est refermé le {limite}. Une réclamation déposée après est irrecevable, quel que soit son bien-fondé. La CFE des années suivantes, elle, reste contestable.",
        motifRefusSeuil: "Votre chiffre d'affaires dépasse le seuil une fois ramené à douze mois. L'article 1647 D du code général des impôts compare les 5 000 € à un chiffre d'affaires annualisé : une activité lancée en cours d'année voit le sien recalculé comme s'il avait couvert l'année entière. Le vôtre s'établit à {annualise}, au-dessus du seuil, et la réclamation serait rejetée.",
        versSimulateur: "Comprendre le calcul de ma CFE",
        avisTitre: "L'avis que vous contestez",
        avisAide: "Le montant et la référence figurent en tête de votre avis, dans votre espace professionnel sur impots.gouv.fr.",
        champMontant: "Montant de la CFE mise à votre charge",
        aideMontant: "En euros, tel qu'il figure sur l'avis. Les centimes sont acceptés.",
        champReferenceAvis: "Référence de l'avis",
        aideReferenceAvis: "Facultative, mais elle accélère le traitement. Elle figure en haut de l'avis.",
        champServiceImpots: "Service des impôts des entreprises",
        aideServiceImpots: "La ville de votre SIE, indiquée sur l'avis. C'est lui qui traite la réclamation.",
        identiteTitre: "Qui réclame ?",
        identiteAide: "Le nom et l'adresse sous lesquels votre entreprise est connue de l'administration.",
        champPrenoms: "Vos prénoms",
        champNom: "Votre nom",
        champSiret: "Numéro SIRET",
        aideSiret: "Quatorze chiffres, espaces autorisés.",
        champVoie: "Numéro et voie",
        champCodePostal: "Code postal",
        champCommune: "Commune",
        signatureTitre: "Où et quand signez-vous ?",
        signatureAide: "L'article R*197-3 du livre des procédures fiscales exige une signature manuscrite. Signez après impression pour un envoi postal ; le paraphe tracé ici convient pour un dépôt depuis votre espace professionnel.",
        champLieuSignature: "Lieu de signature",
        champDateSignature: "Date de signature",
        signatureDessin: "Votre signature",
        signatureDessinAide: "Facultative. Tracez-la à la souris ou au doigt, ou laissez l'emplacement vide pour signer à la main après impression. Elle sera reprise par vos autres documents de la visite.",
        signatureReprise: "Reprise de votre document précédent. Retracez-la par-dessus ou effacez-la si ce n'est pas la bonne.",
        effacer: "Effacer",
        continuer: "Continuer",
        voirDocument: "Voir ma réclamation",
        resultatTitre: "Votre réclamation",
        recapTitre: "Ce que votre lettre demande",
        apercu: "Aperçu du document",
        piecesTitre: "Les pièces à joindre",
        telecharger: "Télécharger en PDF",
        copier: "Copier le texte",
        copie: "Texte copié",
        recommencer: "Recommencer",
        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre lettre.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais leur contenu ne passe pas. Corrigez-les et revenez ici.",
        erreurDateLue: "Nous lisons {date}. Vérifiez l'année.",
        erreurFormat: "Ce format n'est pas reconnu.",
        erreurSiret: "Un SIRET compte quatorze chiffres. Le vôtre en a {compte}.",
        erreurHorsAnnee: "Cette date doit tomber dans l'année que vous contestez, soit {annee}.",
        erreurCreationMemeAnnee: "Née en {annee}, votre entreprise n'a pas de période de référence : c'est l'exonération de l'année de création qu'il faut invoquer.",
        alerteSansReference: "Vous n'avez pas indiqué la référence de l'avis. La réclamation reste recevable, l'année et le SIRET suffisant à désigner l'imposition, mais son traitement sera plus lent.",
        suiteTitre: "Et maintenant",
        suite1: "Déposez la lettre et les pièces depuis la messagerie sécurisée de votre espace professionnel sur impots.gouv.fr, ou envoyez-les à votre service des impôts des entreprises.",
        suite2: "Réclamer ne suspend pas le paiement : si vous n'avez pas encore payé, demandez expressément le sursis de paiement, sans quoi la majoration de 10 % tombera.",
        suite3: "Sans réponse dans les six mois, considérez la réclamation comme rejetée : vous pourrez alors saisir le tribunal administratif.",
        fondTitre: "Ce dont la CFE ne dépend pas",
        fond: "Ni votre bénéfice ni votre local n'entrent dans le calcul de la cotisation minimum : seuls comptent le barème voté par votre commune et votre chiffre d'affaires. Travailler chez soi ne l'annule pas, et un chiffre d'affaires modeste ne la réduit qu'au-dessous de 5 000 €. C'est pour cela que la plupart des réclamations sur le montant échouent.",
    },
    en: {
        phases: ["Your situation", "Your file", "Signature"],
        retour: "Back",
        prive: "Everything happens on your device: the letter is built there, nobody receives your answers, not even us, and it all disappears with the tab.",
        motifTitre: "Why do you think this tax is not owed?",
        motifAide: "Four grounds open a refund to a micro-entrepreneur. Each has its article and its supporting document.",
        motifCreation: "It was my year of registration",
        motifCreationDetail: "No tax is due that year",
        motifPasDeCa: "I had received no turnover at all",
        motifPasDeCaDetail: "The notice came before my first euro",
        motifSeuil: "My turnover is under €5,000",
        motifSeuilDetail: "Exemption from the minimum contribution",
        motifCessation: "I ceased trading during the year",
        motifCessationDetail: "Reduction for the remaining months",
        motifAutre: "Another reason",
        motifAutreDetail: "Or I am not sure",
        anneeTitre: "Which tax year are you contesting?",
        anneeAide: "The year written on the notice, not the year you paid it. The claim deadline depends on it.",
        champAnnee: "Year of the CFE notice",
        delaiRestant: "This assessment can be contested until {limite}.",
        datesTitre: "Your business dates",
        datesAide: "They ground your claim: the tax office will check them against your SIRENE situation notice.",
        champDateCreation: "Date the business was created",
        champDateCessation: "Date you ceased trading",
        aideDateCessation: "The date declared on the single window, within the contested year.",
        champChiffreDAffaires: "Turnover of the reference period",
        aideChiffreDAffaires: "In euros, excluding tax. The reference period is the year before last, or last year if your business did not exist yet.",
        horsSujetTitre: "This claim would not succeed",
        motifRefusAutre: "Business property tax is contested on a precise ground, not on its amount. Only four grounds open a refund to a micro-entrepreneur:",
        motifsRecevables: [
            "The year of registration.",
            "Having collected no turnover at all.",
            "A reference-period turnover of €5,000 or less.",
            "Ceasing trading during the year.",
        ],
        motifRefusAutreFin: "If none applies, the tax is owed, even when it seems disproportionate: the minimum base does not depend on your profit.",
        motifRefusDelai: "The claim period has closed. Article R*196-2 of the tax procedure code sets it at 31 December of the year following collection: for the {annee} tax, it closed on {limite}. A claim filed after that is inadmissible, however well founded. Later years remain contestable.",
        motifRefusSeuil: "Your turnover exceeds the threshold once brought back to twelve months. Article 1647 D of the tax code compares the €5,000 to an annualised turnover: a business started mid-year has its figure recalculated as if it had covered the whole year. Yours comes to {annualise}, above the threshold, and the claim would be rejected.",
        versSimulateur: "Understand how my tax is computed",
        avisTitre: "The notice you are contesting",
        avisAide: "The amount and reference are at the top of your notice, in your professional account on impots.gouv.fr.",
        champMontant: "Amount charged to you",
        aideMontant: "In euros, as written on the notice. Cents are accepted.",
        champReferenceAvis: "Notice reference",
        aideReferenceAvis: "Optional, but it speeds up processing. It appears at the top of the notice.",
        champServiceImpots: "Business tax office",
        aideServiceImpots: "The town of your tax office, shown on the notice. It handles the claim.",
        identiteTitre: "Who is claiming?",
        identiteAide: "The name and address under which the administration knows your business.",
        champPrenoms: "Your forenames",
        champNom: "Your surname",
        champSiret: "SIRET number",
        aideSiret: "Fourteen digits, spaces allowed.",
        champVoie: "Number and street",
        champCodePostal: "Postcode",
        champCommune: "Town",
        signatureTitre: "Where and when are you signing?",
        signatureAide: "Article R*197-3 of the tax procedure code requires a handwritten signature. Sign after printing for a postal claim; the signature drawn here suits a filing from your professional account.",
        champLieuSignature: "Place of signature",
        champDateSignature: "Date of signature",
        signatureDessin: "Your signature",
        signatureDessinAide: "Optional. Draw it with the mouse or your finger, or leave the space empty and sign by hand after printing. Your other documents in this visit will reuse it.",
        signatureReprise: "Taken from your previous document. Draw over it, or clear it if it is not the right one.",
        effacer: "Clear",
        continuer: "Continue",
        voirDocument: "See my claim",
        resultatTitre: "Your claim",
        recapTitre: "What your letter asks for",
        apercu: "Document preview",
        piecesTitre: "The documents to attach",
        telecharger: "Download as PDF",
        copier: "Copy the text",
        copie: "Text copied",
        recommencer: "Start again",
        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your letter.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but their content is not accepted. Correct them and come back.",
        erreurDateLue: "We read {date}. Please check the year.",
        erreurFormat: "This format is not recognised.",
        erreurSiret: "A SIRET has fourteen digits. Yours has {compte}.",
        erreurHorsAnnee: "This date must fall within the year you are contesting, {annee}.",
        erreurCreationMemeAnnee: "Created in {annee}, your business has no reference period: the year-of-creation exemption is the ground to invoke.",
        alerteSansReference: "You did not give the notice reference. The claim remains admissible, the year and SIRET being enough to designate the assessment, but processing will be slower.",
        suiteTitre: "What happens next",
        suite1: "File the letter and documents from the secure messaging of your professional account on impots.gouv.fr, or send them to your business tax office.",
        suite2: "Claiming does not suspend payment: if you have not paid yet, expressly request a stay of payment, otherwise the 10% surcharge will apply.",
        suite3: "With no reply within six months, treat the claim as rejected: you can then take it to the administrative court.",
        fondTitre: "What the tax does not depend on",
        fond: "Neither your profit nor your premises come into the minimum contribution: only the scale voted by your municipality and your turnover count. Working from home does not cancel it, and a modest turnover only reduces it below €5,000. That is why most claims about the amount fail.",
    },
};

const PHASES_PAR_ECRAN = {
    motif: 0,
    annee: 0,
    dates: 0,
    horsSujet: 0,
    avis: 1,
    identite: 1,
    signature: 2,
    resultat: 2,
};

const VIDE = {
    motif: "",
    anneeImposition: "",
    dateCreation: "",
    dateCessation: "",
    chiffreDAffaires: "",
    montant: "",
    referenceAvis: "",
    serviceImpots: "",
    prenoms: "",
    nom: "",
    siret: "",
    voie: "",
    codePostal: "",
    commune: "",
    lieuDeSignature: "",
    dateDeSignature: "",
};

/** Les libellés des champs, hors du dictionnaire lu par le contrôle de parité. */
const LIBELLES_MANQUES = {
    fr: {
        motif: "Le motif de votre réclamation",
        anneeImposition: "L'année de l'avis",
        dateCreation: "La date de création de votre entreprise",
        dateCessation: "La date de cessation d'activité",
        chiffreDAffaires: "Le chiffre d'affaires de la période de référence",
        montant: "Le montant contesté",
        serviceImpots: "Votre service des impôts des entreprises",
        prenoms: "Vos prénoms",
        nom: "Votre nom",
        siret: "Votre numéro SIRET",
        voie: "Votre adresse",
        codePostal: "Votre code postal",
        commune: "Votre commune",
        lieuDeSignature: "Le lieu de signature",
        dateDeSignature: "La date de signature",
    },
    en: {
        motif: "The ground for your claim",
        anneeImposition: "The year of the notice",
        dateCreation: "Your business creation date",
        dateCessation: "The date you ceased trading",
        chiffreDAffaires: "The reference-period turnover",
        montant: "The amount contested",
        serviceImpots: "Your business tax office",
        prenoms: "Your forenames",
        nom: "Your surname",
        siret: "Your SIRET number",
        voie: "Your address",
        codePostal: "Your postcode",
        commune: "Your town",
        lieuDeSignature: "The place of signature",
        dateDeSignature: "The date of signature",
    },
};

export default function ReclamationCfe({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    /* L'identité gardée sur l'appareil préremplit l'écran « identité », que le
       lecteur relit : rien n'est sauté. La réclamation n'a pas de ligne de
       complément d'adresse : le complément rejoint la voie (lib/tools/dossier.js). */
    const departIdentite = () => {
        const depart = { ...VIDE };
        for (const cle of ["prenoms", "nom", "siret", "voie", "codePostal", "commune"]) {
            if (identite[cle]) depart[cle] = identite[cle];
        }
        return depart;
    };
    const [r, setR] = useState(departIdentite);
    const [copie, setCopie] = useState(false);
    const { signature, poser: poserSignature } = useSignature();

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "motif",
        () => {
            // L'identité gardée revient : on recommence le document, pas sa saisie.
            setR(departIdentite());
            setCopie(false);
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    // L'identité se propose à la garde une fois son écran passé.
    useSignalerIdentite(
        {
            prenoms: r.prenoms,
            nom: r.nom,
            siret: r.siret,
            voie: r.voie,
            codePostal: r.codePostal,
            commune: r.commune,
        },
        etape === "signature" || etape === "resultat",
    );

    const jour = String(aujourdhui).slice(0, 10);
    const reponses = useMemo(
        () => ({ ...r, dateDeSignature: r.dateDeSignature || jour }),
        [r, jour],
    );

    const controle = useMemo(() => valider(reponses, aujourdhui), [reponses, aujourdhui]);
    const declaration = useMemo(
        () => composerReclamation(reponses, aujourdhui),
        [reponses, aujourdhui],
    );
    const recap = useMemo(() => recapitulatif(reponses, aujourdhui), [reponses, aujourdhui]);
    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());

    const refusDe = (champ) => (controle.invalides || []).find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        const refus = refusDe(champ);
        if (!refus) return "";
        if (refus.motif === "siret") {
            return remplir(t.erreurSiret, { compte: String(r[champ] || "").replace(/\D/g, "").length });
        }
        if (refus.motif === "horsAnnee") return remplir(t.erreurHorsAnnee, { annee: r.anneeImposition });
        if (refus.motif === "creationMemeAnnee") {
            return remplir(t.erreurCreationMemeAnnee, { annee: r.dateCreation.slice(0, 4) });
        }
        if (refus.motif === "ageImpossible") {
            return remplir(t.erreurDateLue, { date: r[champ] });
        }
        return t.erreurFormat;
    };

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const tri = concerne(r, aujourdhui);
    const { limite } = delaiOuvert(r.anneeImposition, aujourdhui);
    const limiteLisible = limite ? limite.split("-").reverse().join("/") : "";

    const telecharger = () =>
        telechargerDocument({
            document: declaration,
            signature,
            nomFichier: nomDuFichier(`reclamation-cfe-${r.anneeImposition}`, r),
        });

    const copier = async () => {
        if (!declaration) return;
        try {
            await navigator.clipboard.writeText(documentEnTexte(declaration));
            setCopie(true);
            window.setTimeout(() => setCopie(false), 2000);
        } catch {
            // Presse-papiers refusé : le PDF reste la voie normale.
        }
    };

    const slugSimulateur = lang === "en" ? "micro-entreprise-ceiling-calculator" : "simulateur-plafonds-micro-entreprise";

    /* Les années proposées : celle en cours et les deux précédentes, ce que le
       délai de réclamation permet de couvrir dans tous les cas. */
    const anneeCourante = Number(jour.slice(0, 4));
    const annees = [anneeCourante, anneeCourante - 1, anneeCourante - 2];

    /* ---------------------------------------------------------------- */
    /* Les écrans                                                        */
    /* ---------------------------------------------------------------- */

    if (etape === "motif") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.motifTitre} aide={t.motifAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    {[
                        { cle: "anneeCreation", icone: "calendar-linear", titre: t.motifCreation, detail: t.motifCreationDetail },
                        { cle: "pasDeChiffreDAffaires", icone: "wallet-money-linear", titre: t.motifPasDeCa, detail: t.motifPasDeCaDetail },
                        { cle: "seuilCotisationMinimum", icone: "calculator-minimalistic-linear", titre: t.motifSeuil, detail: t.motifSeuilDetail },
                        { cle: "cessation", icone: "close-circle-linear", titre: t.motifCessation, detail: t.motifCessationDetail },
                        { cle: "autre", icone: "info-circle-linear", titre: t.motifAutre, detail: t.motifAutreDetail },
                    ].map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.icone}
                            titre={c.titre}
                            detail={c.detail}
                            onClick={() => {
                                setR((p) => ({ ...p, motif: c.cle }));
                                aller(c.cle === "autre" ? "horsSujet" : "annee");
                            }}
                        />
                    ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "annee") {
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.anneeTitre} aide={t.anneeAide}>
                <div>
                    <Segments
                        libelle={t.champAnnee}
                        options={annees.map((a) => ({ cle: String(a), libelle: String(a) }))}
                        valeur={String(r.anneeImposition)}
                        poser={(v) => setR((p) => ({ ...p, anneeImposition: Number(v) }))}
                    />
                    {r.anneeImposition && limite && (
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                            {remplir(t.delaiRestant, { limite: limiteLisible })}
                        </p>
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!r.anneeImposition}
                    onClick={() => aller(tri.motif === "horsDelai" ? "horsSujet" : "dates")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "dates") {
        const veutCessation = r.motif === "cessation";
        const veutCa = r.motif === "seuilCotisationMinimum";
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.datesTitre} aide={t.datesAide}>
                <div className="space-y-5">
                    <Champ
                        type="date"
                        libelle={t.champDateCreation}
                        valeur={r.dateCreation}
                        poser={poser("dateCreation")}
                        erreur={erreurDe("dateCreation") || erreurDe("motif")}
                    />
                    {veutCessation && (
                        <Champ
                            type="date"
                            libelle={t.champDateCessation}
                            aide={t.aideDateCessation}
                            valeur={r.dateCessation}
                            poser={poser("dateCessation")}
                            erreur={erreurDe("dateCessation")}
                        />
                    )}
                    {veutCa && (
                        <Champ
                            libelle={t.champChiffreDAffaires}
                            aide={t.aideChiffreDAffaires}
                            valeur={r.chiffreDAffaires}
                            poser={poser("chiffreDAffaires")}
                            erreur={erreurDe("chiffreDAffaires")}
                            inputMode="numeric"
                        />
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("dateCreation") ||
                        (veutCessation && !rempli("dateCessation")) ||
                        (veutCa && !rempli("chiffreDAffaires")) ||
                        Boolean(refusDe("dateCreation")) ||
                        Boolean(refusDe("dateCessation")) ||
                        Boolean(refusDe("motif"))
                    }
                    onClick={() => aller(tri.motif === "seuilDepasse" ? "horsSujet" : "avis")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "horsSujet") {
        /* Le refus du seuil montre le chiffre annualisé : « au-dessus du
           seuil » ne dit rien, « 7 957 € » se vérifie. */
        const annualise = montantLisible(
            chiffreDAffairesAnnualise(r.chiffreDAffaires, periodeDeReference(r.anneeImposition, r.dateCreation)),
        );
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {tri.motif === "autre" && t.motifRefusAutre}
                        {tri.motif === "horsDelai" &&
                            remplir(t.motifRefusDelai, { annee: r.anneeImposition, limite: limiteLisible })}
                        {tri.motif === "seuilDepasse" &&
                            remplir(t.motifRefusSeuil, { annualise })}
                    </p>
                    {tri.motif === "autre" && (
                        <>
                            <Liste items={t.motifsRecevables} />
                            <p className="mt-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">
                                {t.motifRefusAutreFin}
                            </p>
                        </>
                    )}
                    {(tri.motif === "autre" || tri.motif === "seuilDepasse") && (
                        <Link
                            href={{ pathname: "/tools/[slug]", params: { slug: slugSimulateur } }}
                            className="inline-block mt-5 text-sm font-semibold text-primary hover:!text-primary"
                        >
                            {t.versSimulateur} →
                        </Link>
                    )}
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "avis") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.avisTitre} aide={t.avisAide}>
                <div className="space-y-5">
                    <Champ
                        libelle={t.champMontant}
                        aide={t.aideMontant}
                        valeur={r.montant}
                        poser={poser("montant")}
                        erreur={erreurDe("montant")}
                        inputMode="decimal"
                    />
                    <Champ
                        libelle={t.champReferenceAvis}
                        aide={t.aideReferenceAvis}
                        valeur={r.referenceAvis}
                        poser={poser("referenceAvis")}
                    />
                    <Champ
                        libelle={t.champServiceImpots}
                        aide={t.aideServiceImpots}
                        valeur={r.serviceImpots}
                        poser={poser("serviceImpots")}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("montant", "serviceImpots") || Boolean(refusDe("montant"))}
                    onClick={() => aller("identite")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "identite") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.identiteTitre} aide={t.identiteAide}>
                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champPrenoms} valeur={r.prenoms} poser={poser("prenoms")} />
                        <Champ libelle={t.champNom} valeur={r.nom} poser={poser("nom")} />
                    </div>
                    <Champ
                        libelle={t.champSiret}
                        aide={t.aideSiret}
                        valeur={r.siret}
                        poser={poser("siret")}
                        erreur={erreurDe("siret")}
                        inputMode="numeric"
                    />
                    <Champ libelle={t.champVoie} valeur={r.voie} poser={poser("voie")} />
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Champ
                            libelle={t.champCodePostal}
                            valeur={r.codePostal}
                            poser={poser("codePostal")}
                            erreur={erreurDe("codePostal")}
                            inputMode="numeric"
                        />
                        <div className="sm:col-span-2">
                            <Champ libelle={t.champCommune} valeur={r.commune} poser={poser("commune")} />
                        </div>
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("prenoms", "nom", "siret", "voie", "codePostal", "commune") ||
                        Boolean(refusDe("siret")) ||
                        Boolean(refusDe("codePostal"))
                    }
                    onClick={() => aller("signature")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.signatureTitre} aide={t.signatureAide}>
                <div className="space-y-5">
                    <Champ
                        libelle={t.champLieuSignature}
                        valeur={r.lieuDeSignature}
                        poser={poser("lieuDeSignature")}
                    />
                    <Champ
                        type="date"
                        libelle={t.champDateSignature}
                        valeur={r.dateDeSignature || jour}
                        poser={poser("dateDeSignature")}
                        erreur={erreurDe("dateDeSignature")}
                    />
                    <PadSignature
                        libelle={t.signatureDessin}
                        aide={t.signatureDessinAide}
                        effacer={t.effacer}
                        repriseLibelle={t.signatureReprise}
                        valeur={signature}
                        poser={poserSignature}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("lieuDeSignature")}
                    onClick={() => aller("resultat")}
                >
                    {t.voirDocument}
                </Avancer>
            </Ecran>
        );
    }

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!declaration ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif }) => (
                            <li key={`${champ}-${motif}`} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                    {" : "}
                                    {erreurDe(champ)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    {controle.alertes.includes("sansReference") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {t.alerteSansReference}
                            </p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.recapTitre}
                    </div>
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                        <table className="w-full text-sm">
                            <tbody>
                                {recap.map(([titre, valeur]) => (
                                    <tr key={titre} className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10">
                                        <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">{titre}</td>
                                        <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.apercu}
                    </div>
                    <ApercuDocument document={declaration} signature={signature} />

                    <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={telecharger}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                        >
                            <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                            {t.telecharger}
                        </button>
                        <button
                            type="button"
                            onClick={copier}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue font-semibold transition-colors duration-200 hover:border-primary/40 hover:text-primary"
                        >
                            <SolarIcon name={copie ? "check-circle-linear" : "copy-linear"} className="text-lg" />
                            {copie ? t.copie : t.copier}
                        </button>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon name="clipboard-check-linear" className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <div className="font-bold dark:text-white leading-snug">{t.piecesTitre}</div>
                        </div>
                        <ul className="space-y-3">
                            {pieces(r).map((p) => (
                                <li key={p} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
                                    <span>{p}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon name="calendar-linear" className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                        </div>
                        <ul className="space-y-3">
                            {[t.suite1, t.suite2, t.suite3].map((ligne) => (
                                <li key={ligne} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                    <span>{ligne}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon name="shield-linear" className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <div className="font-semibold dark:text-white leading-snug">{t.fondTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.fond}</p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
